import React from 'react';
// Rimosso useState, ChevronDown, ChevronRight
import type { LayerBase, LayerStyleProperties } from '../../../../store/types';
import { StylePropertyInput } from '../../../../common/StylePropertyInput';
import type { StyleValue } from '../../../../common/StylePropertyInput';
import { HierarchicalAccordion } from '../../../../common/HierarchicalAccordion';
import { HierarchicalAccordionItem } from '../../../../common/HierarchicalAccordionItem';

interface StyleEditorSectionProps {
  layer: LayerBase & LayerStyleProperties;
  updateLayerStyle: (
    layerId: string,
    propertyName: keyof LayerStyleProperties,
    value: StyleValue
  ) => void;
  isDisabled: boolean;
}

export const CircleLayoutProperties: React.FC<StyleEditorSectionProps> = ({ layer, updateLayerStyle, isDisabled }) => {
  if (layer.maplibreLayerType !== 'circle') return null;
  return (
    <>
      {/* Visibility */}
      <StylePropertyInput
        key={`${layer.id}-visibility`}
        layerId={layer.id}
        propertyName="visibility"
        label="Visibility"
        currentValue={layer.visibility}
        propertyType="select"
        selectOptions={[
          { value: 'visible', label: 'Visible' },
          { value: 'none', label: 'None' },
        ]}
        availablePropertyKeys={layer.availablePropertyKeys}
        onStyleChange={updateLayerStyle}
        isExpressionPossible={true}
        disabled={isDisabled}
      />
      {/* Circle Sort Key */}
      <StylePropertyInput
        key={`${layer.id}-circle-sort-key`}
        layerId={layer.id}
        propertyName="circleSortKey"
        label="Sort Key"
        currentValue={layer.circleSortKey}
        propertyType="number"
        numberConfig={{}}
        availablePropertyKeys={layer.availablePropertyKeys}
        onStyleChange={updateLayerStyle}
        isExpressionPossible={true}
        disabled={isDisabled}
      />
    </>
  );
};

export const CirclePaintProperties: React.FC<StyleEditorSectionProps> = ({ layer, updateLayerStyle, isDisabled }) => {
  if (layer.maplibreLayerType !== 'circle') return null;
  const uniqueIdPrefix = `circle-paint-${layer.id}`;

  return (
    <HierarchicalAccordion style={{ width: '100%' }} defaultOpenId={`${uniqueIdPrefix}-appearance`}>
      <HierarchicalAccordionItem title="Aspetto Base" level={2} id={`${uniqueIdPrefix}-appearance`}>
        <div className="accordion-content" style={{ padding: 'var(--spacing-xs) var(--spacing-sm)'}}>
          {/* Circle Color */}
          <StylePropertyInput
            key={`${layer.id}-circle-color`}
            layerId={layer.id}
            propertyName="circleColor"
            label="Color"
            currentValue={layer.circleColor}
            propertyType="color"
            availablePropertyKeys={layer.availablePropertyKeys}
            onStyleChange={updateLayerStyle}
            isExpressionPossible={true}
            disabled={isDisabled}
          />
          {/* Circle Opacity */}
          <StylePropertyInput
            key={`${layer.id}-circle-opacity`}
            layerId={layer.id}
            propertyName="circleOpacity"
            label="Opacity"
            currentValue={layer.circleOpacity}
            propertyType="range"
            numberConfig={{ min: 0, max: 1, step: 0.01 }}
            availablePropertyKeys={layer.availablePropertyKeys}
            onStyleChange={updateLayerStyle}
            isExpressionPossible={true}
            disabled={isDisabled}
            hideNumericValueDisplay={true}
          />
          {/* Circle Radius */}
          <StylePropertyInput
            key={`${layer.id}-circle-radius`}
            layerId={layer.id}
            propertyName="circleRadius"
            label="Radius"
            currentValue={layer.circleRadius}
            propertyType="number"
            numberConfig={{ min: 0, step: 0.1 }}
            availablePropertyKeys={layer.availablePropertyKeys}
            onStyleChange={updateLayerStyle}
            isExpressionPossible={true}
            disabled={isDisabled}
          />
          {/* Circle Blur */}
          <StylePropertyInput
            key={`${layer.id}-circle-blur`}
            layerId={layer.id}
            propertyName="circleBlur"
            label="Blur"
            currentValue={layer.circleBlur}
            propertyType="number"
            numberConfig={{ min: 0, step: 0.1 }}
            availablePropertyKeys={layer.availablePropertyKeys}
            onStyleChange={updateLayerStyle}
            isExpressionPossible={true}
            disabled={isDisabled}
          />
        </div>
      </HierarchicalAccordionItem>

      <HierarchicalAccordionItem title="Bordo" level={2} id={`${uniqueIdPrefix}-stroke`}>
        <div className="accordion-content" style={{ padding: 'var(--spacing-xs) var(--spacing-sm)'}}>
          {/* Circle Stroke Color */}
          <StylePropertyInput
            key={`${layer.id}-circle-stroke-color`}
            layerId={layer.id}
            propertyName="circleStrokeColor"
            label="Stroke Color"
            currentValue={layer.circleStrokeColor}
            propertyType="color"
            availablePropertyKeys={layer.availablePropertyKeys}
            onStyleChange={updateLayerStyle}
            isExpressionPossible={true}
            disabled={isDisabled}
          />
          {/* Circle Stroke Width */}
          <StylePropertyInput
            key={`${layer.id}-circle-stroke-width`}
            layerId={layer.id}
            propertyName="circleStrokeWidth"
            label="Stroke Width"
            currentValue={layer.circleStrokeWidth}
            propertyType="number"
            numberConfig={{ min: 0, step: 0.1 }}
            availablePropertyKeys={layer.availablePropertyKeys}
            onStyleChange={updateLayerStyle}
            isExpressionPossible={true}
            disabled={isDisabled}
          />
          {/* Circle Stroke Opacity */}
          <StylePropertyInput
            key={`${layer.id}-circle-stroke-opacity`}
            layerId={layer.id}
            propertyName="circleStrokeOpacity"
            label="Stroke Opacity"
            currentValue={layer.circleStrokeOpacity}
            propertyType="range"
            numberConfig={{ min: 0, max: 1, step: 0.01 }}
            availablePropertyKeys={layer.availablePropertyKeys}
            onStyleChange={updateLayerStyle}
            isExpressionPossible={true}
            disabled={isDisabled}
            hideNumericValueDisplay={true}
          />
        </div>
      </HierarchicalAccordionItem>

      <HierarchicalAccordionItem title="Posizionamento Avanzato" level={2} id={`${uniqueIdPrefix}-positioning`}>
        <div className="accordion-content" style={{ padding: 'var(--spacing-xs) var(--spacing-sm)'}}>
          {/* Circle Translate */}
          <StylePropertyInput
            key={`${layer.id}-circle-translate`}
            layerId={layer.id}
            propertyName="circleTranslate"
            label="Translate (X, Y)"
            currentValue={layer.circleTranslate}
            propertyType="array"
            numberConfig={{}}
            availablePropertyKeys={layer.availablePropertyKeys}
            onStyleChange={updateLayerStyle}
            isExpressionPossible={true}
            disabled={isDisabled}
          />
          {/* Circle Translate Anchor */}
          <StylePropertyInput
            key={`${layer.id}-circle-translate-anchor`}
            layerId={layer.id}
            propertyName="circleTranslateAnchor"
            label="Translate Anchor"
            currentValue={layer.circleTranslateAnchor}
            propertyType="select"
            selectOptions={[
              { value: 'map', label: 'Map' },
              { value: 'viewport', label: 'Viewport' },
            ]}
            availablePropertyKeys={layer.availablePropertyKeys}
            onStyleChange={updateLayerStyle}
            isExpressionPossible={true}
            disabled={isDisabled}
          />
          {/* Circle Pitch Scale */}
          <StylePropertyInput
            key={`${layer.id}-circle-pitch-scale`}
            layerId={layer.id}
            propertyName="circlePitchScale"
            label="Pitch Scale"
            currentValue={layer.circlePitchScale}
            propertyType="select"
            selectOptions={[
              { value: 'map', label: 'Map' },
              { value: 'viewport', label: 'Viewport' },
            ]}
            availablePropertyKeys={layer.availablePropertyKeys}
            onStyleChange={updateLayerStyle}
            isExpressionPossible={true}
            disabled={isDisabled}
          />
          {/* Circle Pitch Alignment */}
          <StylePropertyInput
            key={`${layer.id}-circle-pitch-alignment`}
            layerId={layer.id}
            propertyName="circlePitchAlignment"
            label="Pitch Alignment"
            currentValue={layer.circlePitchAlignment}
            propertyType="select"
            selectOptions={[
              { value: 'map', label: 'Map' },
              { value: 'viewport', label: 'Viewport' },
            ]}
            availablePropertyKeys={layer.availablePropertyKeys}
            onStyleChange={updateLayerStyle}
            isExpressionPossible={true}
            disabled={isDisabled}
          />
        </div>
      </HierarchicalAccordionItem>
    </HierarchicalAccordion>
  );
};

const CircleStyleEditor: React.FC<StyleEditorSectionProps> = () => {
  // Questo componente non renderizza più direttamente l'intera UI.
  return null;
};

export default CircleStyleEditor;